import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, merge, of } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';
import { ITableViewModel } from 'src/app/share/entities/view-model';
import { FormAutoCompleteComponent, IFormAutoCompleteViewModel } from 'src/app/share/forms/form-auto-complete/form-auto-complete.component';
import { FormSelectComponent, IDisplaySelectViewModel, IFormSelectChangedArg, IFormSelectViewModel } from 'src/app/share/forms/form-select/form-select.component';
import { BasePageComponent } from 'src/app/share/pages/base-page.component';
import { IPageDataViewModel } from 'src/app/share/pages/pages';
import { AuthService } from 'src/app/share/services/auth.service';
import { CommonService } from 'src/app/share/services/common.service';
import { ShellService } from 'src/app/shell/shell.service';
import { ReportService } from '../services/report.service';

@Component({
  selector: 'app-salesman-reports',
  templateUrl: './salesman-reports.component.html',
  styleUrls: ['./salesman-reports.component.scss']
})
export class SalesmanReportsComponent extends BasePageComponent implements OnInit {

  vm$: Observable<ISalesmanReportPageViewModel>;
  reportTitle: string = 'Trend Report';
  private actionCode: string;
  activeLineIndex: number = 0;

  vmd: IViewModel;

  private monthOptions: IDisplaySelectViewModel = undefined;

  private pageCommandBs$: BehaviorSubject<string> = new BehaviorSubject<string>('initialize');
  
  constructor(private route: ActivatedRoute, private commonService: CommonService, private authService: AuthService, protected shellService: ShellService,
    private reportService: ReportService) { 
    super(shellService, route);
  }

  ngOnInit(): void {

    const line: string = this.route.snapshot.paramMap.get("line");
    if (!isNaN(Number(line)))
      this.activeLineIndex = Number(line) > 0 ? Number(line) - 1 : 0;
    
    this.actionCode = this.route.snapshot.paramMap.get("type");

    this.reportTitle = this.setTitle(this.actionCode);

    this.monthOptions = this.setMonthOptions();

    this.vmd = this.initViewModel();

    // content display
    const loader$: Observable<ISalesmanReportPageViewModel> = of({
      filterData:  {
        pickupMonth: -1,
        customerCode: '*',
        parentGroupCode: '*',
        salesPersonCode: '*',
        completedOnly: false,
        readyForBilling: false
      }
    }).pipe(
      switchMap(ctx => this.reportVm$(this.actionCode, ctx.filterData).pipe(
        map((rvm: IReportViewModel) => {
          return {
            ...ctx,
            reportVm: rvm
          };
        })
      )),
      map(ctx => { 
        const ret: ISalesmanReportPageViewModel = {
          actionsAllowed: true, canExportExcel: false, actionCode: this.route.snapshot.paramMap.get("type"),
          showFilter: this.actionCode == 'salesman-new-accounts' ? false : true,
          filterVm: {
            data: ctx.filterData,
            //customerCodeAutoCompleteVm: FormAutoCompleteComponent.viewModel(ctx.filterData, 'customerCode',
            //  { title: 'Customer', apiPath: '/api/reservations', apiAction: 'auto-complete-customer' }),
            //parentGroupCodeAutoCompleteVm: FormAutoCompleteComponent.viewModel(ctx.filterData, 'parentGroupCode',
            //  { title: 'Parent Group', apiPath: '/api/reservations', apiAction: 'auto-complete-parent-group' }),
            salesPersonCodeAutoCompleteVm: FormAutoCompleteComponent.viewModel(ctx.filterData, 'salesPersonCode',
              { title: 'Sales Person', apiPath: '/api/reservations', apiAction: 'auto-complete-ecls-sales-person' }),
            pickupMonthSelectVm: FormSelectComponent.viewModel(ctx.filterData, 'pickupMonth',
              { title: 'Month', isRequired: false, type: 'select' }, this.monthOptions),
            yearToCompareVm: FormSelectComponent.viewModel(ctx.filterData, 'yearToCompare',
              { title: 'Year to Compare', isRequired: false, type: 'select' }, this.reportService.setYearToCompare())
          },
          reportVm: ctx.reportVm
        }
        return ret;
      })
    );

    // commands
    const comand$: Observable<ISalesmanReportPageViewModel> = this.pageCommandBs$.pipe(
      switchMap(cmdText => { 
        const ret: ISalesmanReportPageViewModel = {
          actionsAllowed: false, canExportExcel: false, actionCode: this.actionCode,
          filterVm: undefined,
          reportVm: {
            workingText: cmdText == 'initialize' ? 'Initializing...' : cmdText == 'refresh' ? 'Refreshing...' : 'Loading...',
            errorText: undefined
          }
        };

        if (cmdText == 'refresh')
          return merge(of(ret), loader$);
        else
          return of(ret);
      })
    );

    this.vm$ = merge(comand$, loader$);
  }

  setTitle(type: string): string {
    switch(type) {
      case 'revenue-by-salesman': return 'Revenue by Salesman';
      case 'salesman-new-accounts': return 'Accounts Open in 12 Month';
    }
    return 'Salesman Report';
  }

  /**
   * 
   * @param action 
   * @returns 
   */
  private reportVm$(action: string, filterData: ITrendReportFilterData): Observable<IReportViewModel> {
    return of({}).pipe(
      switchMap(ctx => this.commonService.getData('PageCommand', 'salesman.report', action, filterData, '/api/SantaCruz', true)),
      map((v: IReportViewModel) => {
        if (v.workingText)
          return { ...v };
        const ret = { ...v };
        if (ret.data) {

          ret.lastDateTimeUpdated = ret.lastDateTimeUpdated,

          ret.tabVms = [];
  
          if (ret.data.revenue && !ret.data.revenue.unauthorized) {
            ret.tabVms.push({
              active: false,
              title: ret.data.revenue.title,
              searchableColumns: ret.data.revenue.searchableColumns,
              detailsPageCommandCode: ret.data.revenue.detailsPageCommandCode,
              detailsPageCommandAction: ret.data.revenue.detailsPageCommandAction,
              tableVm: {
                ...ret.data.revenue,
                totalsFiltered: ret.data.revenue.totals ? [...ret.data.revenue.totals] : [],
                rowsFiltered: [...ret.data.revenue.rows],
                searchValue: ''
              }
            })
          }
          if (ret.data.gainers && !ret.data.gainers.unauthorized) {
            ret.tabVms.push({
              active: false,
              title: ret.data.gainers.title,
              searchableColumns: ret.data.revenue.searchableColumns,
              detailsPageCommandCode: ret.data.gainers.detailsPageCommandCode,
              detailsPageCommandAction: ret.data.gainers.detailsPageCommandAction,
              tableVm: {
                ...ret.data.gainers,
                totalsFiltered: ret.data.gainers.totals ? [...ret.data.gainers.totals] : [],
                rowsFiltered: [...ret.data.gainers.rows],
                searchValue: ''
              }
            })
          }
          if (ret.data.decliners && !ret.data.decliners.unauthorized) {
            ret.tabVms.push({
              active: false,
              title: ret.data.decliners.title, 
              searchableColumns: ret.data.revenue.searchableColumns,
              detailsPageCommandCode: ret.data.decliners.detailsPageCommandCode,
              detailsPageCommandAction: ret.data.decliners.detailsPageCommandAction,
              tableVm: {
                ...ret.data.decliners,
                totalsFiltered: ret.data.decliners.totals ? [...ret.data.decliners.totals] : [],
                rowsFiltered: [...ret.data.decliners.rows],
                searchValue: ''
              }
            })
          }
          if (ret.data.revenueTotalByYear && !ret.data.revenueTotalByYear.unauthorized) {
            ret.tabVms.push({
              active: false,
              title: ret.data.revenueTotalByYear.title,
              searchableColumns: ret.data.revenue.searchableColumns,
              detailsPageCommandCode: ret.data.revenueTotalByYear.detailsPageCommandCode,
              detailsPageCommandAction: ret.data.revenueTotalByYear.detailsPageCommandAction,
              tableVm: {
                ...ret.data.revenueTotalByYear,
                totalsFiltered: ret.data.revenueTotalByYear.totals ? [...ret.data.revenueTotalByYear.totals] : [],
                rowsFiltered: [...ret.data.revenueTotalByYear.rows],
                searchValue: ''
              }
            })
          } 
        }
        return ret;
      }),
      catchError((err) => of({ workingText: undefined, errorText: err}))
    );
  }

  /**
   * 
   */
  refreshPage(pvm: ISalesmanReportPageViewModel) {
    this.pageCommandBs$.next('refresh');
  }

  setMonthOptions(): IDisplaySelectViewModel {
    const ret: IDisplaySelectViewModel = {
      description: 'End Month',
      options: [
        { label: 'January', value: '1', icon: '' },
        { label: 'February', value: '2', icon: '' },
        { label: 'March', value: '3', icon: '' },
        { label: 'April', value: '4', icon: '' },
        { label: 'May', value: '5', icon: '' },
        { label: 'June', value: '6', icon: '' },
        { label: 'July', value: '7', icon: '' },
        { label: 'August', value: '8', icon: '' },
        { label: 'September', value: '9', icon: '' },
        { label: 'October', value: '10', icon: '' },
        { label: 'November', value: '11', icon: '' },
        { label: 'December', value: '12', icon: '' }
      ]
    };
    return ret;
  }

  /**
   * 
   * @param event 
   */
  onGetActiveTab(pvm: ISalesmanReportPageViewModel, event: any) { 
    const title = event?.el?.heading?.length ? event.el.heading : '';

    pvm.reportVm.tabVms.forEach(tvm => tvm.active = tvm.title == title);

    this.vmd.selectActiveLineVm = this.createSelectActiveLineVm('activeLineIndex', 'Data Type to Highlight', title, { activeLineIndex: this.activeLineIndex });
  }

  /**
   * 
   * @param event 
   */
  onGetDetailsActiveTab(event: any) { 
    const title = event?.el?.heading?.length ? event.el.heading : '';

    this.vmd.detailsFormVm.tabDetailsVms.forEach(tvm => tvm.active = tvm.title == title);
  }

  /**
   * 
   */
  async doSearch(pvm: ISalesmanReportPageViewModel) {
    try {
      pvm.reportVm.workingText = 'Filtering...';
      pvm.reportVm.errorText = undefined;

      var result = await of({}).pipe(
        delay(500),
        switchMap(ctx => this.reportVm$(pvm.actionCode, pvm.filterVm.data))
      ).toPromise();

      pvm.reportVm = result;

    } catch (err) {
      pvm.reportVm.errorText = err;
    } finally {
      pvm.reportVm.workingText = undefined;
    }
  }

  async showDetails(d: any) {
    this.vmd.showTable = false;
    this.vmd.detailsFormVm = this.initDetailsFormVm();
    this.showDetailsStart(d.code, d.data, d.baseCode, d.baseAction);
  }

  /**
   * 
   * @param pvm 
   * @param vm 
   */
  private async showDetailsStart(code: any, vm: ISalesmanReportPageViewModel, baseCode: string, baseAction: string) {
    
    try {
      this.vmd.detailsFormVm.workingText = 'Filtering...';

      var origSalesPersonCode = vm.filterVm.data.salesPersonCode;
      var filterData: ITrendReportFilterData = vm.filterVm.data;
      filterData.salesPersonCode = code;

      this.vmd.detailsFormVm.tabDetailsVms = [];

      var ret = await of({}).pipe(
        delay(500),
        switchMap(ctx => this.commonService.getData('PageCommand', baseCode, baseAction, filterData, '/api/SantaCruz', true)),
      ).toPromise();

      vm.filterVm.data.salesPersonCode = origSalesPersonCode;
      if (ret.data) {
        if (ret.data.revenue && !ret.data.revenue.unauthorized) {
          this.vmd.detailsFormVm.tabDetailsVms.push({
            active: false,
            title: ret.data.revenue.title,
            searchableColumns: ret.data.revenue.searchableColumns,
            tableVm: {
              ...ret.data.revenue,
              totalsFiltered: ret.data.revenue.totals ? [...ret.data.revenue.totals] : [],
              rowsFiltered: [...ret.data.revenue.rows],
            }
          })
        }
        if (ret.data.gainers && !ret.data.gainers.unauthorized) {
          this.vmd.detailsFormVm.tabDetailsVms.push({
            active: false,
            title: ret.data.gainers.title,
            searchableColumns: ret.data.revenue.searchableColumns,
            tableVm: {
              ...ret.data.gainers,
              totalsFiltered: ret.data.gainers.totals ? [...ret.data.gainers.totals] : [],
              rowsFiltered: [...ret.data.gainers.rows],
              searchValue: ''
            }
          })
        }
        if (ret.data.decliners && !ret.data.decliners.unauthorized) {
          this.vmd.detailsFormVm.tabDetailsVms.push({
            active: false,
            title: ret.data.decliners.title,
            searchableColumns: ret.data.revenue.searchableColumns,
            tableVm: {
              ...ret.data.decliners,
              totalsFiltered: ret.data.decliners.totals ? [...ret.data.decliners.totals] : [],
              rowsFiltered: [...ret.data.decliners.rows],
              searchValue: ''
            }
          })
        }
        if (ret.data.affiliatesMargin && !ret.data.affiliatesMargin.unauthorized) {
          this.vmd.detailsFormVm.tabDetailsVms.push({
            active: false,
            title: ret.data.affiliatesMargin.title,
            searchableColumns: ret.data.revenue.searchableColumns,
            tableVm: {
              ...ret.data.affiliatesMargin,
              totalsFiltered: ret.data.affiliatesMargin.totals ? [...ret.data.affiliatesMargin.totals] : [],
              rowsFiltered: [...ret.data.affiliatesMargin.rows],
              searchValue: ''
            }
          })
        }
        if (ret.data.revenueTotalByYear && !ret.data.revenueTotalByYear.unauthorized) {
          this.vmd.detailsFormVm.tabDetailsVms.push({
            active: false,
            title: ret.data.revenueTotalByYear.title,
            searchableColumns: ret.data.revenue.searchableColumns,
            tableVm: {
              ...ret.data.revenueTotalByYear,
              totalsFiltered: ret.data.revenueTotalByYear.totals ? [...ret.data.revenueTotalByYear.totals] : [],
              rowsFiltered: [...ret.data.revenueTotalByYear.rows],
              searchValue: ''
            }
          })
        }
      }

    } catch (err) {
      this.vmd.detailsFormVm.errorText = err;
    } finally {
      this.vmd.detailsFormVm.workingText = undefined;
    }
  }

  showDetailsEnd() {
    this.vmd.showTable = true;
    this.vmd.detailsFormVm = undefined;
  }

  initViewModel(): IViewModel {
    const data: IViewModelData = { activeLineIndex: this.activeLineIndex };
    const ret: IViewModel = {
      data: data,
      showTable: true,
      detailsFormVm: undefined,
      selectActiveLineVm: this.createSelectActiveLineVm('activeLineIndex', 'Data Type to Highlight', '', data)
    }
    return ret;
  }

  createSelectActiveLineVm(fieldId: string, title: string, tabTitle: string, data: IViewModelData): IFormSelectViewModel {
    return FormSelectComponent.viewModel(data, fieldId,
    { title: title, isRequired: false, type: 'select' }, this.reportService.setLineTypes(tabTitle))
  }

  initDetailsFormVm(): IDetailFormViewModel {
    const ret: IDetailFormViewModel = {
      cancelText: "Close",
      workingText: undefined,
      errorText: undefined,
      tabDetailsVms: undefined
    }
    return ret;
  }

  yearToCompareChanged(e: IFormSelectChangedArg, vm: IViewModel) {

  }

  selectActiveLineChanged(e: IFormSelectChangedArg, vm: IViewModel) {
    if (!isNaN(Number(e.option.value)))
      this.activeLineIndex = Number(e.option.value) > 0 ? Number(e.option.value) : 0;
  }
}

/**
 * 
 */
interface ISalesmanReportPageViewModel extends IPageDataViewModel {
  reportVm?: IReportViewModel;

  actionsAllowed?: boolean;
  showFilter?: boolean;
    
  filterVm: {
    data: ITrendReportFilterData;
    //customerCodeAutoCompleteVm?: IFormAutoCompleteViewModel;
    //parentGroupCodeAutoCompleteVm?: IFormAutoCompleteViewModel;
    salesPersonCodeAutoCompleteVm?: IFormAutoCompleteViewModel;
    pickupMonthSelectVm?: IFormSelectViewModel;
    yearToCompareVm?: IFormSelectViewModel;
  }

  isValid?: boolean;
}

/**
 * 
 */
interface ITrendReportFilterData {
  pickupMonth: number;
  customerCode: string;
  parentGroupCode: string;
  salesPersonCode: string;
  completedOnly?: boolean;
  readyForBilling?: boolean;
  yearToCompare?: number;
}

/**
 * 
 */
interface IReportViewModel {
  data?: {
    revenueTotalByYear: ITableViewModel, revenue: ITableViewModel, gainers: ITableViewModel, decliners: ITableViewModel
  };

  tabVms?: IReportTabViewModel[];

  lastDateTimeUpdated?: string;
  workingText?: string;
  errorText?: string;
}

/**
 * 
 */
interface IReportTabViewModel {
  title: string;
  tableVm: ITableViewModel;
  active: boolean;
  detailsPageCommandCode?: string;
  detailsPageCommandAction?: string;
  searchableColumns: number[];
}

interface IViewModel {
  data: IViewModelData;
  detailsFormVm?: IDetailFormViewModel;
  showTable: boolean;
  selectActiveLineVm: IFormSelectViewModel;
}
/**
 * 
 */
interface IDetailFormViewModel {
  cancelText: string;
  errorText: string;
  workingText: string;

  tabDetailsVms?: IReportTabViewModel[];
}

interface IViewModelData {
  activeLineIndex: number;
}